import React, {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from 'react'
import {
  AlphaCollection,
  NumericCollection,
} from '../../components/Keyboard/types'
import { useHybridFocusable } from '../../hooks/useHybridFocusable'
import { useAppDispatch } from '../../hooks/useStore'
import type {
  ChangeStationHeaderRef,
  ChangeStationScreenHeaderProps,
} from './types'
import tts from '../../lib/TTS'

const SAVE_SEARCH_QUERY_ACTION_NAME = 'SAVE_SEARCH_QUERY'

const ChangeStationHeader = forwardRef<
  ChangeStationHeaderRef,
  ChangeStationScreenHeaderProps
>(
  (
    {
      children,
      handleSearchCleared,
      onZipCodeEntered,
      onDigitAdded,
    }: ChangeStationScreenHeaderProps,
    ref
  ) => {
    const [searchQuery, setSearchQuery] = useState('')
    const dispatch = useAppDispatch()
    const oldSearchQuery = useRef('')
    const MAX_LENGTH = 5

    useImperativeHandle(ref, () => ({
      addDigit: (digit: string) => {
        setSearchQuery((prev) =>
          prev.length < MAX_LENGTH ? prev + digit : prev
        )
        onDigitAdded?.(digit)
      },
    }))

    const { ref: header } = useHybridFocusable({
      focusKey: 'CHANGE_STATION_SCREEN_HEADER',
      trackChildren: true,
      onBlur: (_arg1, _arg2, { event }) => {
        if (event && (event as KeyboardEvent).key === 'ArrowDown') {
          dispatch({
            type: SAVE_SEARCH_QUERY_ACTION_NAME,
            payload: { searchQuery },
          })
        }
      },
    })

    useEffect(() => {
      handleSearchQueryChange()

      searchQuery.length >= 5 &&
        tts.addTextToRead(
          `Searching for available stations for zip code: ${searchQuery
            .split('')
            .join(' , ')}`
        )
    }, [searchQuery])

    const handleSearchQueryChange = () => {
      const searchQueryChanged = searchQuery !== oldSearchQuery.current
      const searchQueryCleared =
        searchQuery.length === 0 && oldSearchQuery.current.length > 0

      if (searchQueryChanged && searchQuery.length === MAX_LENGTH) {
        onZipCodeEntered(searchQuery)
      }

      if (searchQueryCleared) {
        handleSearchCleared()
      }

      if (searchQueryChanged) {
        oldSearchQuery.current = searchQuery
      }
    }

    const onEnterPress = useCallback(
      (keyString: NumericCollection | AlphaCollection) => {
        if (keyString === NumericCollection.spaceNumeric) {
          setSearchQuery((prev) => prev + ' ')
        } else if (keyString === NumericCollection.deleteNumeric) {
          setSearchQuery((prev) => prev.slice(0, -1))
        } else if (
          Object.values(NumericCollection).includes(
            keyString as NumericCollection
          )
        ) {
          setSearchQuery((prev) =>
            prev.length < MAX_LENGTH ? prev + keyString : prev
          )
        }
      },
      [setSearchQuery]
    )

    return (
      <div className="change-station-container__header" ref={header}>
        <div className="change-station-container__search-container">
          {children(searchQuery, onEnterPress)}
        </div>
      </div>
    )
  }
)

ChangeStationHeader.displayName = 'ChangeStationHeader'

export default ChangeStationHeader
